import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import API from '../../../api/api'
import {
  mapBookingInfo,
  mapCustomerInformation,
  mapAdministrativeInfo,
  mapPaymentInformation,
  filterOutUnavailableSpaces,
} from '../helpers'
import ModalContainer from '../../ModalContainer/ModelContainer'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import Icon from '../../Icons'
import DetailsItem from '../components/DetailsItem/DetailsItem'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

const ReservationsDetailsModal = ({
  onClose = () => {},
  refreshReservations = () => {},
  timezone,
  reservationId,
}) => {
  const { t } = useTranslation(['reservations'])
  const [isLoading, setIsLoading] = useState(true)
  const [reservation, setReservation] = useState(null)
  const [locations, setLocations] = useState([])
  const [selectedLocation, setSelectedLocation] = useState(null)
  const { displaySnackbar } = useSnackbar()

  const items = reservation
    ? [
        {
          data: mapBookingInfo(reservation, timezone, locations, setSelectedLocation, t),
          title: t('reservations:reservationsDetailsModal.bookingInformation'),
        },
        {
          data: mapCustomerInformation(reservation, t),
          title: t('reservations:reservationsDetailsModal.customerInformation'),
        },
        {
          data: mapPaymentInformation(reservation, timezone, t),
          title: t('reservations:reservationsDetailsModal.paymentInformation'),
        },
        {
          data: mapAdministrativeInfo(reservation, timezone, t),
          title: t('reservations:reservationsDetailsModal.administrativeInformation'),
        },
      ]
    : []

  const fetchLocations = async () => {
    try {
      const params = reservation.amenityId.multiDayBooking
        ? { date: reservation.date, endDate: reservation.endDate }
        : {
            timestamp: reservation.date,
            startTime: reservation.startTime,
            endTime: reservation.endTime,
          }

      const {
        data: { locations = [] },
      } = await API.getAmenityLocations(reservation?.amenityId?.id, params)
      setLocations(filterOutUnavailableSpaces(locations, reservation))
    } catch ({ message }) {
      console.error(message)
    }
  }

  const fetchDetails = async () => {
    try {
      setIsLoading(true)
      const result = await API.getReservationDetails(reservationId)
      setReservation(result.data)
      setIsLoading(false)
    } catch ({ message }) {
      console.error(message)
    }
  }

  const updateReservation = async (notifyEmail = false) => {
    try {
      setIsLoading(true)
      await API.updateReservation(reservationId, { locationId: selectedLocation }, notifyEmail)
      await fetchDetails()
      refreshReservations()
      if (notifyEmail) {
        displaySnackbar(t('reservations:reservationsDetailsModal.residentNotified'))
      } else {
        displaySnackbar(t('reservations:reservationsDetailsModal.reservationUpdated'))
      }
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(t('reservations:reservationsDetailsModal.errorUpdating'), true)
    } finally {
      setIsLoading(false)
      setSelectedLocation(null)
    }
  }

  useEffect(() => {
    fetchDetails()
  }, [reservationId])

  useEffect(() => {
    if (reservation?.amenityId?.id) {
      fetchLocations()
    }
  }, [reservation])

  return (
    <>
      <ModalContainer
        headerTitle={t('reservations:reservationsDetailsModal.headerTitle')}
        onClose={onClose}
        additionalClass={`reservations-modal ${isLoading ? 'is-loading' : ''}`}
        headerIcon={<Icon icon="notebook" viewBox="0 0 18 18" />}
      >
        <div className="reservations-modal-content reservations-details-modal">
          {items.map((item, index) => (
            <DetailsItem data={item.data} title={item.title} key={index} />
          ))}
        </div>
      </ModalContainer>
      <ConfirmPopup
        title={t('reservations:conflictReservations.confirmationTitle')}
        body={t('reservations:reservationsDetailsModal.notifyResidentBody', {
          previousSpace: reservation?.locationId?.name,
          newSpace: locations.find((location) => location.id === selectedLocation)?.name,
        })}
        cancelText={t('reservations:amenityForms.no')}
        confirmText={t('reservations:amenityForms.yes')}
        className="is-caution"
        isOpen={!!selectedLocation}
        cancelCallback={() => updateReservation(false)}
        confirmCallback={() => updateReservation(true)}
      />
    </>
  )
}

ReservationsDetailsModal.propTypes = {
  onClose: PropTypes.func,
  timezone: PropTypes.string,
  reservationId: PropTypes.number,
  refreshReservations: PropTypes.func,
}

export default ReservationsDetailsModal
